/**
 * GridCrash — Leaderboard
 * localStorage-backed per-tier top-10, with a transparent in-memory fallback
 * for private browsing, quota exhaustion, or any storage exception.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LeaderboardEntry {
  score: number;
  tier: string;
  timestamp: number;
  mode: "random" | "deterministic";
}

const STORAGE_VERSION = "v2";
const TOP_N = 10;

// ─── In-memory fallback ───────────────────────────────────────────────────────

const memoryStore: Map<string, LeaderboardEntry[]> = new Map();

// ─── Storage helpers ──────────────────────────────────────────────────────────

function storageKey(tierKey: string): string {
  return `gridcrash.lb.${STORAGE_VERSION}.${tierKey}`;
}

function readFromStorage(tierKey: string): LeaderboardEntry[] {
  try {
    const raw = localStorage.getItem(storageKey(tierKey));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Validate shape of each entry
    return parsed.filter(
      (e): e is LeaderboardEntry =>
        typeof e === "object" &&
        e !== null &&
        typeof (e as LeaderboardEntry).score === "number" &&
        typeof (e as LeaderboardEntry).timestamp === "number"
    );
  } catch {
    return [];
  }
}

function writeToStorage(tierKey: string, entries: LeaderboardEntry[]): void {
  try {
    localStorage.setItem(storageKey(tierKey), JSON.stringify(entries));
    // Mirror to memory so reads after a quota error still work
    memoryStore.set(tierKey, entries);
  } catch {
    // QuotaExceededError or SecurityError (private browsing) — use memory only
    memoryStore.set(tierKey, entries);
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Load top-N entries for a tier, sorted descending by score.
 * Falls back to in-memory store if localStorage is unavailable.
 */
export function getTopScores(tierKey: string): LeaderboardEntry[] {
  // Try localStorage first; fall back to memory mirror
  const stored = readFromStorage(tierKey);
  if (stored.length > 0) return stored;
  return memoryStore.get(tierKey) ?? [];
}

/**
 * Add a new score for a tier.
 * Returns the updated top-N list and the achieved rank (1-based), or null if
 * the score did not qualify.
 */
export function addScore(
  entry: Omit<LeaderboardEntry, "timestamp">
): { entries: LeaderboardEntry[]; rank: number | null } {
  const full: LeaderboardEntry = { ...entry, timestamp: Date.now() };
  const existing = getTopScores(entry.tier);
  const combined = [...existing, full];

  // Sort descending
  combined.sort((a, b) => b.score - a.score);

  const top = combined.slice(0, TOP_N);
  writeToStorage(entry.tier, top);

  const rank = top.findIndex(
    (e) => e.score === full.score && e.timestamp === full.timestamp
  );

  return {
    entries: top,
    rank: rank === -1 ? null : rank + 1, // 1-based
  };
}

/**
 * Clear all scores for a tier.
 */
export function clearScores(tierKey: string): void {
  try {
    localStorage.removeItem(storageKey(tierKey));
  } catch {
    // ignore
  }
  memoryStore.delete(tierKey);
}

/**
 * Format a score entry's date as a relative string (e.g., "2 days ago").
 */
export function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

/**
 * Format elapsed seconds as MM:SS.
 */
export function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Rank medal emoji for top positions.
 */
export function rankLabel(rank: number): string {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return `#${rank}`;
}
